/**
 * Draws the metric panel over the video.
 *
 * The renderer permanently mirrors the 2D context so the camera image reads
 * correctly, which means anything drawn through it inherits that flip and comes
 * out backwards. Text especially. Every draw here happens inside a
 * save/setTransform/restore so the panel is laid out in ordinary screen
 * coordinates, and the mirror is put back untouched for the next frame.
 *
 * Set in the same bitmap face as the rest of the UI. A canvas never triggers a
 * font load of its own — it silently falls back — but the DOM is set in this face
 * too, so by the time the model has downloaded and this first draws, it is there.
 * The face is roughly twice as wide per character as a proportional one, which is
 * why every size here is smaller and the panel is wider than it looks like it
 * needs to be: "Straightest elbow" is seventeen characters.
 */
const FACE = '"Press Start 2P", ui-monospace, monospace';
const PADDING = 12;
const LINE_HEIGHT = 20;
const PANEL_WIDTH = 300;
/** The rep count has to be readable from across a gym, unlike the metrics. */
const COUNT_HEIGHT = 44;
/** The same without the progress bar, which a timed drill has no use for. */
const COUNT_HEIGHT_BARE = 28;
const PASS_COLOR = "#3ddc6b";
const FAIL_COLOR = "#e8384f";
const DIM_COLOR = "#8d9bd4";

export class Readout {
  constructor(canvas) {
    this.ctx = canvas.getContext("2d");
  }

  /**
   * @param view The declared camera view, shown so a mis-set view is obvious.
   * @param results Output of `evaluate()`.
   * @param note Optional status line, e.g. why values are missing.
   * @param counter Optional headline figure under the rows:
   *   `{ value, label, progress }`. `value` and `label` are already strings
   *   because what a drill counts is the drill's business — a hold's headline is
   *   a clock in seconds and labelling it REPS is simply wrong, which is what
   *   the handstand shipped. `progress` null draws no bar: a hold's bar fills in
   *   two seconds and then sits full for the rest of a forty-second hold.
   * @param posture Optional rows from `PostureTally.summary()`.
   */
  draw(view, results, note, counter = null, posture = null) {
    const ctx = this.ctx;
    ctx.save();
    // Drop the renderer's mirror for the duration of the panel.
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    const postureRows = posture || [];
    const rows = results.length + 1 + postureRows.length;
    const height =
      PADDING * 2 +
      rows * LINE_HEIGHT +
      (note ? LINE_HEIGHT : 0) +
      (counter
        ? counter.progress == null
          ? COUNT_HEIGHT_BARE
          : COUNT_HEIGHT
        : 0);

    // Flat fill and a hard edge, matching the bars: no blur anywhere in this UI.
    ctx.fillStyle = "rgba(5, 7, 15, 0.82)";
    ctx.fillRect(0, 0, PANEL_WIDTH, height);
    ctx.fillStyle = "#05070f";
    ctx.fillRect(0, height - 4, PANEL_WIDTH, 4);
    ctx.fillRect(PANEL_WIDTH - 4, 0, 4, height);

    ctx.textBaseline = "top";
    ctx.font = `10px ${FACE}`;
    ctx.fillStyle = "#ffd23f";
    ctx.fillText(`${view} view`.toUpperCase(), PADDING, PADDING);

    ctx.font = `8px ${FACE}`;
    results.forEach((metric, i) => {
      const y = PADDING + (i + 1) * LINE_HEIGHT;
      ctx.fillStyle = DIM_COLOR;
      ctx.fillText(metric.label.toUpperCase(), PADDING, y);

      const shown =
        metric.value == null
          ? "--"
          : `${metric.value.toFixed(metric.precision)}${metric.unit}`;
      ctx.fillStyle = metric.value == null ? "#5c6694" : "#e8f0ff";
      ctx.textAlign = "right";
      ctx.fillText(shown, PANEL_WIDTH - PADDING, y);
      ctx.textAlign = "left";
    });

    postureRows.forEach((row, i) => {
      const y = PADDING + (results.length + 1 + i) * LINE_HEIGHT;
      ctx.fillStyle = DIM_COLOR;
      ctx.fillText(row.label.toUpperCase(), PADDING, y);

      // The tally, not just this rep: ten red marks on a ten-rep set is noise,
      // whereas "8/10" is something a coach can act on. An informational row has
      // no tally to show and must not be coloured as a pass or a fault — it gets
      // its own note in the neutral amber the other read-this-number uses.
      const shown =
        row.value == null
          ? "--"
          : row.informational
            ? `${row.value.toFixed(row.precision)}${row.unit}  ${row.note}`
            : `${row.value.toFixed(row.precision)}${row.unit}  ${row.passed}/${row.total}`;
      ctx.fillStyle = row.informational
        ? "#ffd23f"
        : row.pass == null
          ? "#5c6694"
          : row.pass
            ? PASS_COLOR
            : FAIL_COLOR;
      ctx.textAlign = "right";
      ctx.fillText(shown, PANEL_WIDTH - PADDING, y);
      ctx.textAlign = "left";
    });

    if (note) {
      ctx.fillStyle = "#5c6694";
      ctx.font = `8px ${FACE}`;
      ctx.fillText(note.toUpperCase(), PADDING, PADDING + rows * LINE_HEIGHT);
    }

    if (counter) {
      const y = PADDING + rows * LINE_HEIGHT + (note ? LINE_HEIGHT : 0);
      ctx.font = `24px ${FACE}`;
      // Amber, like the count in the bar: the same figure in the same colour in
      // both places, so there is nothing to reconcile.
      ctx.fillStyle = "#ffd23f";
      ctx.fillText(counter.value, PADDING, y);
      ctx.font = `8px ${FACE}`;
      ctx.fillStyle = "#5c6694";
      ctx.fillText(
        counter.label,
        PADDING + 20 + counter.value.length * 24,
        y + 16
      );

      if (counter.progress != null) {
        // A bar rather than a number: mid-rep progress is for reassuring the
        // athlete the counter is alive, not for reading a value off.
        // Drawn as cells rather than a sliver, matching the loading bar: a block
        // appearing reads as progress at a glance, a sliver growing does not.
        const barY = y + 34;
        const barWidth = PANEL_WIDTH - PADDING * 2;
        const cells = 20;
        const cell = Math.floor(barWidth / cells);
        const filled = Math.round(Math.min(counter.progress, 1) * cells);
        for (let i = 0; i < cells; i += 1) {
          ctx.fillStyle = i < filled ? "#ffd23f" : "rgba(141, 155, 212, 0.22)";
          ctx.fillRect(PADDING + i * cell, barY, cell - 2, 6);
        }
      }
    }

    ctx.restore();
  }
}
